import { useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { TbTrash, TbCheck, TbX, TbPencil } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import { apiRequest, ApiError } from "src/services/apiClient";
import { useApi } from "src/hooks/useApi";
import { getTagStyle } from "src/helpers/randomColor";
import type { Group } from "src/types";
import "../Invoices/manageTagsPopup.scss";

export function ManageGroupsPopup({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { data: groups, loading, refetch } = useApi<Group[]>("/groups");
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [savingId, setSavingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);

    const handleCreate = async (e: FormEvent) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        try {
            await apiRequest("/groups", { method: "POST", body: JSON.stringify({ name }) }, { "Content-Type": "application/json" });
            setNewName("");
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("groups.createFailed"), variant: "error" });
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (group: Group) => { setEditingId(group.id); setEditName(group.name); };
    const cancelEdit = () => { setEditingId(null); setEditName(""); };

    const handleRename = async (id: string) => {
        const name = editName.trim();
        if (!name) return;
        setSavingId(id);
        try {
            await apiRequest(`/groups/${id}`, { method: "PUT", body: JSON.stringify({ name }) }, { "Content-Type": "application/json" });
            setEditingId(null);
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("groups.renameFailed"), variant: "error" });
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await apiRequest(`/groups/${deleteTarget.id}`, { method: "DELETE" });
            setDeleteTarget(null);
            await refetch();
        } catch (err) {
            setToast({ message: err instanceof ApiError ? err.message : t("groups.deleteFailed"), variant: "error" });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            <Popup
                title={t("groups.manageGroups")}
                className="manage-tags-popup"
                onClose={onClose}
                actions={[{ label: t("popup.done"), variant: "primary", onClick: onClose }]}
                message={
                    <div className="manage-tags">
                        <form className="manage-tags__add" onSubmit={handleCreate}>
                            <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={t("groups.newGroupPlaceholder")}
                                maxLength={40}
                            />
                            <button type="submit" className="popup__btn popup__btn--primary" disabled={creating || !newName.trim()}>
                                {t("groups.addGroup")}
                            </button>
                        </form>

                        {loading ? (
                            <p className="manage-tags__empty">{t("common.loading")}</p>
                        ) : !groups?.length ? (
                            <p className="manage-tags__empty">{t("groups.noGroups")}</p>
                        ) : (
                            <ul className="manage-tags__list">
                                {groups.map((group) => (
                                    <li key={group.id} className="manage-tags__row">
                                        <span
                                            className="manage-tags__chip"
                                            style={getTagStyle(group.name) as CSSProperties}
                                        >
                                            {group.name}
                                        </span>
                                        {editingId === group.id ? (
                                            <div className="manage-tags__edit">
                                                <input
                                                    type="text"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    maxLength={40}
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn"
                                                    onClick={() => handleRename(group.id)}
                                                    disabled={savingId === group.id || !editName.trim()}
                                                >
                                                    <TbCheck />
                                                </button>
                                                <button type="button" className="manage-tags__icon-btn" onClick={cancelEdit}>
                                                    <TbX />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="manage-tags__actions">
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn"
                                                    onClick={() => startEdit(group)}
                                                    title={t("groups.rename")}
                                                >
                                                    <TbPencil />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="manage-tags__icon-btn manage-tags__icon-btn--danger"
                                                    onClick={() => setDeleteTarget(group)}
                                                    title={t("groups.delete")}
                                                >
                                                    <TbTrash />
                                                </button>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                }
            />

            {deleteTarget && (
                <Popup
                    title={t("groups.delete")}
                    message={t("groups.deleteConfirm", { name: deleteTarget.name })}
                    onClose={() => setDeleteTarget(null)}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setDeleteTarget(null) },
                        { label: t("popup.ok"), variant: "danger", disabled: deleting, onClick: handleDelete },
                    ]}
                />
            )}
        </>
    );
}

export default ManageGroupsPopup;
